
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import CompleteProfileModal from '../../components/PrimeiroAcesso';
import { useAuthContext } from '../../context/AuthContext';

// Função utilitária para redirecionar usuário para sua área
function buildRedirectFromUser(user, forceAdmin = false) {
  if (!user) return '/';
  if (user.roles && user.roles.includes('admin') && forceAdmin) {
    return '/admin';
  }
  // Slugify igual ao padrão do projeto
  const slugify = (text) =>
    (text || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '')
      .replace(/--+/g, '-');
  if (user.diocese && user.paroquia && user.comunidade && user.tipo) {
    return `/diocese/${slugify(user.diocese)}/paroquia/${slugify(user.paroquia)}/comunidade/${slugify(user.comunidade)}/tipo/${slugify(user.tipo)}`;
  }
  return '/';
}

function normalizeRedirectPath(path) {
  if (!path || typeof path !== 'string') return null;
  // Garante que não exista "//" no pathname (React Router normaliza, mas isso pode quebrar rotas)
  // Mantém querystring intacta.
  const [pathname, query] = path.split('?');
  const safePathname = (pathname || '').replace(/\/{2,}/g, '/');
  const rebuilt = query ? `${safePathname}?${query}` : safePathname;
  return rebuilt.startsWith('/') ? rebuilt : `/${rebuilt}`;
}

function extractRoutePartsFromRedirect(path) {
  // Espera padrão: /diocese/:d/paroquia/:p/comunidade/:c/tipo/:t[/...]
  const safe = normalizeRedirectPath(path);
  if (!safe) return null;
  const match = safe.match(/\/diocese\/([^/]+)\/paroquia\/([^/]+)\/comunidade\/([^/]+)\/tipo\/([^/?#]+)/i);
  if (!match) return null;
  const [, dioceseSlug, paroquiaSlug, comunidadeSlug, tipoSlug] = match;
  return {
    diocese: dioceseSlug,
    paroquia: paroquiaSlug,
    comunidade: comunidadeSlug,
    tipo: tipoSlug,
  };
}

export default function LoginPage() {
  // Função para gerar horário local no formato 'YYYY-MM-DD HH:mm:ss'
  function getLocalDateTimeString() {
    const d = new Date();
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOnlyAno, setModalOnlyAno] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const navigate = useNavigate();
  const { setUser } = useAuthContext();


  useEffect(() => {
    // Não redirecionar automaticamente enquanto o modal estiver aberto
    if (isModalOpen) return;

    if (token) {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user) {
        // Se o usuário é admin, garante que está em /admin e nunca redireciona para rota de membro
        if (user && user.roles && user.roles.includes('admin')) {
          if (window.location.pathname !== '/admin') {
            navigate('/admin', { replace: true });
          }
          return;
        }

        // Se o backend já forneceu um redirect válido (ex.: para Coroinhas/Coordenadores), use-o
        const lastRedirect = normalizeRedirectPath(localStorage.getItem('lastRedirect'));
        if (!user.diocese && lastRedirect && window.location.pathname !== lastRedirect) {
          navigate(lastRedirect, { replace: true });
          return;
        }

        // Definir membroPath para não-admins
        const membroPath = buildRedirectFromUser(user);
        // Se não está na rota de membro, redireciona (apenas para não-admin)
        if (user && window.location.pathname !== membroPath) {
          navigate(membroPath, { replace: true });
        }
      }
    }
  }, [token, navigate, isModalOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e) => {
  e.preventDefault();
  setError('');
  setLoading(true);

  // Usa horário local do dispositivo no formato 'YYYY-MM-DD HH:mm:ss'
  const horario_dispositivo = getLocalDateTimeString();

  try {
    const { data } = await api.post('/login', {
      email: form.email,
      password: form.password,
      horario_dispositivo 
    });

      const receivedToken = data?.token ?? data?.access_token ?? data?.userAuth?.token ?? null;
      if (receivedToken) {
        localStorage.setItem('token', receivedToken);
        setToken(receivedToken);
      }

      if (data.user) {
        // Garante que grupo_id e campos essenciais estejam presentes
        const backendRedirect = data?.redirect ?? data?.redirectTo ?? null;
        const inferred = extractRoutePartsFromRedirect(backendRedirect);

        const userCompleto = {
          ...data.user,
          grupo_id: data.user.grupo_id || data.user.grupo?.id || '',
          diocese: data.user.diocese || data.user.diocese_nome || inferred?.diocese || '',
          paroquia: data.user.paroquia || data.user.paroquia_nome || '',
          comunidade: data.user.comunidade || data.user.comunidade_nome || '',
          tipo: data.user.tipo || data.user.tipo_usuario || '',
        };
        localStorage.setItem('user', JSON.stringify(userCompleto));
        if (setUser) setUser(userCompleto); // Atualiza o contexto imediatamente
      } else {
      }

      if (data.requiresProfileUpdate) {
        setModalOnlyAno(false);
        setIsModalOpen(true);
        return;
      } else {
        // Modal reduzido: apenas ano_investidura (somente Acolitos)
        const userStored = JSON.parse(localStorage.getItem('user') || '{}');
        const tipoLower = (userStored.tipo || '').toString().toLowerCase();
        const isAcolito = tipoLower === 'acolitos' || tipoLower === 'acolito' || tipoLower.includes('acolito');
        const anoNull = userStored.ano_investidura === null || userStored.ano_investidura === undefined || userStored.ano_investidura === '';
        const primeiroLoginRaw = data?.primeiro_login ?? data?.user?.primeiro_login ?? null;
        const primeiroLoginZero = primeiroLoginRaw === false || Number(primeiroLoginRaw) === 0;

        if (isAcolito && anoNull && primeiroLoginZero) {
          setModalOnlyAno(true);
          setIsModalOpen(true);
          return;
        }

        const backendRedirect = normalizeRedirectPath(data?.redirect ?? data?.redirectTo ?? null);
        if (backendRedirect) {
          localStorage.setItem('lastRedirect', backendRedirect);
          navigate(backendRedirect, { replace: true });
          return;
        }
        const user = JSON.parse(localStorage.getItem('user'));
        if (user.roles && user.roles.includes('admin')) {
          navigate('/admin', { replace: true });
        } else {
          const redirectPath = buildRedirectFromUser(user, true);
          navigate(redirectPath, { replace: true });
        }
      }
    } catch (err) {
      setError('E-mail ou senha incorretos.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdateSuccess = () => {
    setIsModalOpen(false);
    setForm({ email: '', password: '' }); // Limpa o formulário
    setSuccessMessage('Dados atualizados com sucesso! Faça login novamente.');
    
    // Remove a mensagem após 5 segundos
    setTimeout(() => {
      setSuccessMessage('');
    }, 5000);
  };

  return (
    <>
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-sm">
          <h1 className="text-2xl font-bold text-center mb-6 text-gray-800">Login</h1>
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                E-mail
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={form.email}
                onChange={handleChange}
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Digite seu e-mail"
                required
              />
            </div>

            <div className="relative">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Senha
              </label>
              <div className="mt-1 w-full flex items-center relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  id="password"
                  value={form.password}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  placeholder="Digite sua senha"
                  required
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-600 focus:outline-none"
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPassword ? (
                    // Ícone de olho aberto (Heroicons outline)
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5C7.305 4.5 3.135 7.36 1.5 12c1.635 4.64 5.805 7.5 10.5 7.5s8.865-2.86 10.5-7.5C20.865 7.36 16.695 4.5 12 4.5z" />
                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  ) : (
                    // Ícone de olho fechado (Heroicons outline)
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M10.477 10.477A3 3 0 0112 9c1.657 0 3 1.343 3 3 0 .523-.134 1.015-.366 1.44M6.53 6.53C4.06 8.36 2.5 10.94 2.5 12c1.635 4.64 5.805 7.5 10.5 7.5 2.01 0 3.93-.44 5.57-1.22M17.47 17.47C19.94 15.64 21.5 13.06 21.5 12c-1.635-4.64-5.805-7.5-10.5-7.5-1.13 0-2.23.13-3.27.37" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="text-right">
              <Link to="/recuperar-senha" className="text-sm font-medium text-blue-600 hover:text-blue-500 hover:underline">
                Esqueci minha senha
              </Link>
            </div>

            {error && (
              <div className="text-red-500 text-sm text-center">{error}</div>
            )}

            {successMessage && (
              <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded-lg border border-green-200">
                {successMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center items-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-lg transition duration-300 ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'
                }`}
            >
              {loading && (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              )}
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        </div>
      </div>

      <CompleteProfileModal
        isOpen={isModalOpen}
        onlyAno={modalOnlyAno}
        onSuccess={handleProfileUpdateSuccess}
      />
    </>
  );
}